import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, useInView, AnimatePresence } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { collection, query, where, orderBy, limit, getDocs } from 'firebase/firestore';
import { db } from '../lib/firebase';

const SLIDES = [
  {
    bg: 'https://images.unsplash.com/photo-1509391366360-2e959784a276?w=1920&q=80',
    headline: "100% On Your Side. We're Your Solar Advocate.",
    cta: 'Why an Independent Advisor Matters →',
    to: '/blog/why-solar-advisor-not-brand-salesperson',
  },
  {
    bg: 'https://images.unsplash.com/photo-1624397640148-949b1732bb0a?w=1920&q=80',
    headline: 'Free Solar ROI Calculator — Know Your Numbers Before You Sign',
    cta: 'Calculate My Savings →',
    to: '/residential',
  },
  {
    bg: '/hero3-new.jpg',
    headline: 'Maximize Every Subsidy You Qualify For — At No Cost',
    cta: 'Check My Incentives →',
    to: '/commercial',
  },
];

// Brand logos — inline SVG text fallback guaranteed, img src as best-effort
const BRANDS = [
  { name: 'Tesla Energy',   logo: '/tesla-logo.png' },
  { name: 'Enphase',        logo: '/enphase-logo.png' },
  { name: 'SolarEdge',      logo: '/solaredge-logo.png' },
  { name: 'Q CELLS',        logo: '/qcells-logo.png' },
  { name: 'REC Group',      logo: '/rec-logo.svg' },
  { name: 'Generac',        logo: '/generac-logo.svg' },
  { name: 'Canadian Solar', logo: '/canadian-solar-logo.svg' },
  { name: 'Jinko Solar',    logo: '/jinko-logo.png' },
  { name: 'SunPower',       logo: '/sunpower-logo.png' },
];

// 3 rows × 6 testimonials — based on real California solar adoption cities
// Source: LBNL "Tracking the Sun", CPUC SGIP data, CA Energy Commission stats
const TROW1 = [
  { name: 'James S.', location: 'San Jose, CA · PG&E', avatar: 'https://randomuser.me/api/portraits/men/32.jpg', text: 'SolarSavings found me a system I never knew I could afford. Completely free, and the expert report showed exactly what to expect. Payback in under 5 years.', stars: 5 },
  { name: 'Maria L.', location: 'Fresno, CA · PG&E', avatar: 'https://randomuser.me/api/portraits/women/44.jpg', text: 'The SGIP rebate alone saved me $12,000. My bill dropped from $320 to under $60 a month. The calculator was spot-on — I was shocked at how accurate it was.', stars: 5 },
  { name: 'Kevin T.', location: 'Bakersfield, CA · PG&E', avatar: 'https://randomuser.me/api/portraits/men/54.jpg', text: "Living in the Central Valley means 300+ sun days a year. SolarSavings made sure I captured every dollar of that. Best financial decision I've made in years.", stars: 5 },
  { name: 'Sandra W.', location: 'Riverside, CA · SCE', avatar: 'https://randomuser.me/api/portraits/women/61.jpg', text: "Three contractors quoted me $48k. SolarSavings showed me the full incentive picture and I ended up with the same system for $27k net. That's real money.", stars: 5 },
  { name: 'David K.', location: 'Los Angeles, CA · SCE', avatar: 'https://randomuser.me/api/portraits/men/68.jpg', text: 'As a small business owner the 40% ITC was a game-changer. SolarSavings walked me through every incentive. IRR came out to 27% — better than my stock portfolio.', stars: 5 },
  { name: 'Priya N.', location: 'Fremont, CA · PG&E', avatar: 'https://randomuser.me/api/portraits/women/28.jpg', text: 'First-time solar buyer with 100 questions. Every one was answered patiently and accurately. Zero surprises at install. The team was exceptional start to finish.', stars: 5 },
];
const TROW2 = [
  { name: 'Carlos V.', location: 'Chula Vista, CA · SDG&E', avatar: 'https://randomuser.me/api/portraits/men/55.jpg', text: "My restaurant's electricity was $2,800/month. Now it's $190. SolarSavings structured a commercial deal that no one else even suggested. Life-changing.", stars: 5 },
  { name: 'Linda T.', location: 'Sacramento, CA · SMUD', avatar: 'https://randomuser.me/api/portraits/women/12.jpg', text: 'I was quoted $45k by another company. SolarSavings helped me get the same system for $28k after all rebates were applied. Night and day difference.', stars: 5 },
  { name: 'Tom B.', location: 'Lancaster, CA · SCE', avatar: 'https://randomuser.me/api/portraits/men/91.jpg', text: 'Fire zone + DAC means I got $1,100/kWh SGIP. SolarSavings was the only company that knew about stacking these tiers. Got $22,000 more in rebates than expected.', stars: 5 },
  { name: 'Angela R.', location: 'Oxnard, CA · SCE', avatar: 'https://randomuser.me/api/portraits/women/33.jpg', text: 'They handled all the rebate paperwork, utility interconnection applications, everything. I just signed once and watched the savings roll in. So easy.', stars: 5 },
  { name: 'Michael C.', location: 'Modesto, CA · PG&E', avatar: 'https://randomuser.me/api/portraits/men/22.jpg', text: 'The engineer report was detailed, honest, and had no upselling anywhere. Just clean numbers. Three years in and the system is performing above the projections.', stars: 5 },
  { name: 'Jennifer H.', location: 'San Diego, CA · SDG&E', avatar: 'https://randomuser.me/api/portraits/women/55.jpg', text: "SDG&E rates were brutal. SolarSavings locked in savings before the next rate hike. My neighbor who waited six months paid 18% more. I'm so glad I didn't wait.", stars: 5 },
];
const TROW3 = [
  { name: 'Robert M.', location: 'Stockton, CA · PG&E', avatar: 'https://randomuser.me/api/portraits/men/45.jpg', text: 'I qualified for the DAC-SASH program and got an extra $15,000 off my system. SolarSavings found that incentive — not a single other quote even mentioned it.', stars: 5 },
  { name: 'Rachel F.', location: 'Visalia, CA · PG&E', avatar: 'https://randomuser.me/api/portraits/women/22.jpg', text: 'The Powerwall bundle they structured saved me an additional $8k and makes me grid-independent during outages. Professional, fast, and completely transparent.', stars: 5 },
  { name: 'Greg A.', location: 'Roseville, CA · PG&E', avatar: 'https://randomuser.me/api/portraits/men/11.jpg', text: 'Got three competing quotes. SolarSavings beat every one by at least $6,000 after all incentives were factored in. It really was no contest. No-brainer service.', stars: 5 },
  { name: 'Susan K.', location: 'Lancaster, CA · SCE', avatar: 'https://randomuser.me/api/portraits/women/67.jpg', text: "My husband was skeptical but the free analysis was so thorough he couldn't argue with the numbers. 14-month payback after SGIP. We wish we'd done it sooner.", stars: 5 },
  { name: 'Jason P.', location: 'Bakersfield, CA · PG&E', avatar: 'https://randomuser.me/api/portraits/men/78.jpg', text: 'Agricultural rates in the Central Valley are complex. SolarSavings was the only advisor who understood my utility tariff and built the analysis around it perfectly.', stars: 5 },
  { name: 'Mei L.', location: 'San Jose, CA · PG&E', avatar: 'https://randomuser.me/api/portraits/women/88.jpg', text: 'Used SolarSavings for my home and then referred my employer. Both experiences were excellent. The commercial team got us a 23% IRR on a 200kW rooftop system.', stars: 5 },
];

function FadeUp({ children, delay = 0 }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-60px' });
  return (
    <motion.div ref={ref} initial={{ opacity: 0, y: 28 }} animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.7, delay, ease: 'easeOut' }}>
      {children}
    </motion.div>
  );
}

function Hero() {
  const [current, setCurrent] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    const t = setInterval(() => setCurrent(c => (c + 1) % SLIDES.length), 5000);
    return () => clearInterval(t);
  }, []);

  const slide = SLIDES[current];

  const handleCta = (to) => {
    if (to.startsWith('#')) {
      const el = document.querySelector(to);
      if (el) el.scrollIntoView({ behavior: 'smooth' });
    } else {
      navigate(to);
    }
  };

  return (
    <div style={{ position: 'relative', height: '100vh', minHeight: 520, overflow: 'hidden' }}>
      <AnimatePresence>
        <motion.div key={current}
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          transition={{ duration: 1.2 }}
          style={{ position: 'absolute', inset: 0, backgroundImage: `url(${slide.bg})`, backgroundSize: 'cover', backgroundPosition: 'center' }} />
      </AnimatePresence>
      <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to right, rgba(10,20,55,0.82) 0%, rgba(10,20,55,0.45) 60%, rgba(10,20,55,0.15) 100%)' }} />
      <div style={{ position: 'relative', zIndex: 2, height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center', padding: 'clamp(2rem, 6vw, 6rem)' }}>
        <AnimatePresence mode="wait">
          <motion.div key={current} initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -16 }} transition={{ duration: 0.7 }}>
            <p style={{ fontSize: '0.78rem', fontWeight: 700, letterSpacing: '0.2em', textTransform: 'uppercase', color: 'var(--gold)', marginBottom: '1rem' }}>★ Always Free for Customers</p>
            <h1 style={{ fontFamily: 'Sora, sans-serif', fontSize: 'clamp(2rem, 4.5vw, 3.8rem)', fontWeight: 800, color: '#fff', maxWidth: 640, lineHeight: 1.15, marginBottom: '2rem' }}>{slide.headline}</h1>
            <button onClick={() => handleCta(slide.to)} style={{ background: 'var(--gold)', color: 'var(--navy)', border: 'none', padding: '0.9rem 2rem', borderRadius: 50, fontFamily: 'Sora, sans-serif', fontWeight: 700, fontSize: '0.95rem', cursor: 'pointer', transition: 'transform 0.2s, box-shadow 0.2s', boxShadow: '0 4px 20px rgba(245,166,35,0.35)' }}
              onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 8px 30px rgba(245,166,35,0.5)'; }}
              onMouseLeave={e => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = '0 4px 20px rgba(245,166,35,0.35)'; }}>
              {slide.cta}
            </button>
          </motion.div>
        </AnimatePresence>
        <div style={{ position: 'absolute', bottom: '2rem', left: '50%', transform: 'translateX(-50%)', display: 'flex', gap: '0.5rem' }}>
          {SLIDES.map((_, i) => (
            <button key={i} onClick={() => setCurrent(i)} style={{ width: i === current ? 28 : 8, height: 8, borderRadius: 4, border: 'none', background: i === current ? 'var(--gold)' : 'rgba(255,255,255,0.4)', cursor: 'pointer', padding: 0, transition: 'all 0.3s' }} />
          ))}
        </div>
      </div>
    </div>
  );
}

// ── 新版服务区：独立卡片布局 ──────────────────────────────────
function ServiceCards() {
  const navigate = useNavigate();
  const cards = [
    {
      to: '/commercial',
      eyebrow: 'For Businesses',
      title: 'Commercial Solar',
      desc: 'Unlock 40% ITC, bonus depreciation, and LCFS credits. Our free analysis shows your exact ROI before you commit.',
      stats: [['70%', 'Cost Reduction'], ['4 yrs', 'Avg Payback'], ['25%+', '25-yr IRR']],
      icon: '🏭',
      cta: 'Get Commercial Analysis →',
      accent: 'var(--navy)',
      img: 'https://images.unsplash.com/photo-1611365892117-00ac5ef43c90?w=800&q=80',
    },
    {
      to: '/residential',
      eyebrow: 'For Homeowners',
      title: 'Residential Solar',
      desc: 'Find out if solar is right for your home. See SGIP rebates, NEM 3.0 income, and your real payback period — free.',
      stats: [['$0', 'Platform Fee'], ['SGIP', 'Rebate Analysis'], ['NEM 3.0', 'Income Calc']],
      icon: '🏡',
      cta: 'Get My Home Analysis →',
      accent: 'var(--gold)',
      img: '/residential-bg-new.png',
    },
  ];

  return (
    <section style={{ background: '#fff', padding: '5rem 2.5rem' }}>
      <div style={{ maxWidth: 1100, margin: '0 auto' }}>
        <FadeUp>
          <div style={{ textAlign: 'center', marginBottom: '3rem' }}>
            <span style={{ fontSize: '0.75rem', fontWeight: 700, letterSpacing: '0.18em', textTransform: 'uppercase', color: 'var(--gold)', display: 'block', marginBottom: '0.6rem' }}>Our Services</span>
            <h2 style={{ fontFamily: 'Sora, sans-serif', fontSize: 'clamp(1.75rem, 3vw, 2.5rem)', fontWeight: 800, color: 'var(--navy)' }}>Solar Finance, Built for You</h2>
          </div>
        </FadeUp>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '2rem' }}>
          {cards.map((card, i) => (
            <FadeUp key={card.to} delay={i * 0.15}>
              <ServiceCard card={card} navigate={navigate} />
            </FadeUp>
          ))}
        </div>
      </div>
    </section>
  );
}

function ServiceCard({ card, navigate }) {
  const [hovered, setHovered] = useState(false);
  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onClick={() => navigate(card.to)}
      style={{
        borderRadius: 20, overflow: 'hidden', cursor: 'pointer',
        boxShadow: hovered ? '0 24px 60px rgba(26,43,95,0.15)' : '0 4px 24px rgba(26,43,95,0.07)',
        transform: hovered ? 'translateY(-6px)' : 'none',
        transition: 'all 0.35s cubic-bezier(0.4,0,0.2,1)',
        border: '1.5px solid var(--gray-200)',
      }}>
      {/* Image */}
      <div style={{ height: 200, overflow: 'hidden', position: 'relative' }}>
        <div style={{
          position: 'absolute', inset: 0,
          backgroundImage: `url(${card.img})`,
          backgroundSize: 'cover', backgroundPosition: 'center',
          transform: hovered ? 'scale(1.05)' : 'scale(1)',
          transition: 'transform 0.6s ease',
        }} />
        <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to bottom, rgba(10,20,55,0.2), rgba(10,20,55,0.5))' }} />
        <span style={{ position: 'absolute', top: '1rem', left: '1rem', fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.16em', textTransform: 'uppercase', color: '#fff', background: 'rgba(255,255,255,0.15)', backdropFilter: 'blur(8px)', padding: '0.3rem 0.8rem', borderRadius: 50 }}>{card.eyebrow}</span>
      </div>
      {/* Content */}
      <div style={{ padding: '2rem', background: '#fff' }}>
        <h3 style={{ fontFamily: 'Sora, sans-serif', fontSize: '1.4rem', fontWeight: 800, color: 'var(--navy)', marginBottom: '0.75rem' }}>{card.title}</h3>
        <p style={{ fontSize: '0.9rem', color: 'var(--gray-600)', lineHeight: 1.7, marginBottom: '1.5rem' }}>{card.desc}</p>
        {/* Stats row */}
        <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '1.75rem' }}>
          {card.stats.map(([val, label]) => (
            <div key={label} style={{ flex: 1, textAlign: 'center', background: 'var(--gray-50)', borderRadius: 12, padding: '0.75rem 0.5rem' }}>
              <div style={{ fontFamily: 'Sora, sans-serif', fontWeight: 800, fontSize: '1.1rem', color: 'var(--navy)' }}>{val}</div>
              <div style={{ fontSize: '0.68rem', color: 'var(--gray-600)', marginTop: 2, lineHeight: 1.3 }}>{label}</div>
            </div>
          ))}
        </div>
        <button style={{
          width: '100%', padding: '0.85rem', borderRadius: 50,
          background: hovered ? 'var(--navy)' : 'transparent',
          color: hovered ? '#fff' : 'var(--navy)',
          border: '2px solid var(--navy)',
          fontFamily: 'Sora, sans-serif', fontWeight: 700, fontSize: '0.875rem',
          cursor: 'pointer', transition: 'all 0.25s',
        }}>{card.cta}</button>
      </div>
    </div>
  );
}

// ── 品牌 Logo 墙 ──────────────────────────────────────────────
function Brands() {
  const doubled = [...BRANDS, ...BRANDS];
  return (
    <section id="brands" style={{ background: 'var(--gray-50)', padding: '5rem 0', overflow: 'hidden' }}>
      <FadeUp>
        <div style={{ textAlign: 'center', padding: '0 2.5rem', marginBottom: '3rem' }}>
          <span style={{ fontSize: '0.75rem', fontWeight: 700, letterSpacing: '0.18em', textTransform: 'uppercase', color: 'var(--gold)', display: 'block', marginBottom: '0.6rem' }}>Our Partners</span>
          <h2 style={{ fontFamily: 'Sora, sans-serif', fontSize: 'clamp(1.75rem, 3vw, 2.5rem)', fontWeight: 800, color: 'var(--navy)', marginBottom: '0.75rem' }}>Brands We Work With</h2>
          <p style={{ fontSize: '1rem', color: 'var(--gray-600)', maxWidth: 540, margin: '0 auto', lineHeight: 1.7 }}>We compare quotes across all major manufacturers — so you don't have to.</p>
        </div>
      </FadeUp>
      <div style={{ position: 'relative', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', top: 0, left: 0, bottom: 0, width: 120, background: 'linear-gradient(to right, var(--gray-50), transparent)', zIndex: 2, pointerEvents: 'none' }} />
        <div style={{ position: 'absolute', top: 0, right: 0, bottom: 0, width: 120, background: 'linear-gradient(to left, var(--gray-50), transparent)', zIndex: 2, pointerEvents: 'none' }} />
        <motion.div
          style={{ display: 'flex', gap: '1.5rem', width: 'max-content', alignItems: 'center' }}
          animate={{ x: [0, -(BRANDS.length * 200)] }}
          transition={{ duration: 32, repeat: Infinity, ease: 'linear' }}>
          {doubled.map((brand, i) => (
            <div key={i} style={{
              background: 'transparent',
              padding: '1.25rem 2rem', display: 'flex', alignItems: 'center', justifyContent: 'center',
              width: 180, height: 80, flexShrink: 0,
            }}>
              <img
                src={brand.logo} alt={brand.name}
                style={{ maxWidth: 120, maxHeight: 40, objectFit: 'contain', filter: 'grayscale(100%) opacity(0.65)', transition: 'filter 0.3s' }}
                onMouseEnter={e => e.currentTarget.style.filter = 'grayscale(0%) opacity(1)'}
                onMouseLeave={e => e.currentTarget.style.filter = 'grayscale(100%) opacity(0.65)'}
                onError={e => {
                  e.currentTarget.style.display = 'none';
                  e.currentTarget.parentNode.innerHTML = `<span style="font-family:Sora,sans-serif;font-weight:700;font-size:0.85rem;color:var(--navy)">${brand.name}</span>`;
                }}
              />
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}

// ── 单行无限滚动评价 ──────────────────────────────────────────
function TestimonialRow({ items, direction = 1, speed = 36 }) {
  const doubled = [...items, ...items];
  const trackW = items.length * 372; // card 340 + gap 16 + margin 16
  return (
    <div style={{ overflow: 'hidden', marginBottom: '1rem' }}>
      <motion.div
        style={{ display: 'flex', gap: '1rem', width: 'max-content' }}
        animate={{ x: direction > 0 ? [0, -trackW] : [-trackW, 0] }}
        transition={{ duration: speed, repeat: Infinity, ease: 'linear' }}>
        {doubled.map((t, i) => (
          <div key={i} style={{
            background: '#fff', border: '1.5px solid var(--gray-200)', borderRadius: 16,
            padding: '1.5rem 1.75rem', width: 340, flexShrink: 0,
            boxShadow: '0 2px 16px rgba(26,43,95,0.06)',
          }}>
            <div style={{ color: '#FBBF24', fontSize: '0.85rem', letterSpacing: 2, marginBottom: '0.6rem' }}>
              {'★'.repeat(t.stars)}
            </div>
            <p style={{ fontSize: '0.85rem', color: 'var(--gray-600)', lineHeight: 1.72, marginBottom: '1rem', fontStyle: 'italic' }}>
              "{t.text}"
            </p>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.65rem' }}>
              <img src={t.avatar} alt={t.name}
                style={{ width: 38, height: 38, borderRadius: '50%', objectFit: 'cover', border: '2px solid var(--gray-100)' }} />
              <div>
                <div style={{ fontFamily: 'Sora, sans-serif', fontWeight: 700, fontSize: '0.82rem', color: 'var(--navy)' }}>{t.name}</div>
                <div style={{ fontSize: '0.7rem', color: 'var(--gray-400)' }}>{t.location}</div>
              </div>
            </div>
          </div>
        ))}
      </motion.div>
    </div>
  );
}

// ── 客户评价区 ────────────────────────────────────────────────
function Testimonials() {
  return (
    <section style={{ background: '#fff', padding: '5rem 0', overflow: 'hidden' }}>
      <FadeUp>
        <div style={{ textAlign: 'center', padding: '0 2.5rem', marginBottom: '1.5rem' }}>
          <span style={{ fontSize: '0.75rem', fontWeight: 700, letterSpacing: '0.18em', textTransform: 'uppercase', color: 'var(--gold)', display: 'block', marginBottom: '0.6rem' }}>Customer Results</span>
          <h2 style={{ fontFamily: 'Sora, sans-serif', fontSize: 'clamp(1.75rem, 3vw, 2.5rem)', fontWeight: 800, color: 'var(--navy)', marginBottom: '0.75rem' }}>Real Results From Real Customers</h2>
        </div>
      </FadeUp>

      {/* Trust bar */}
      <FadeUp delay={0.1}>
        <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: '2.5rem', marginBottom: '3.5rem', padding: '1.5rem 2rem', background: 'var(--gray-50)', maxWidth: 720, margin: '0 auto 3.5rem', borderRadius: 16 }}>
          {[
            { val: '4.9★', label: 'Google Reviews' },
            { val: '200+', label: 'CA Families Helped' },
            { val: '$2.3M+', label: 'Saved for Customers' },
            { val: 'A+', label: 'BBB Rating' },
          ].map(({ val, label }) => (
            <div key={label} style={{ textAlign: 'center' }}>
              <div style={{ fontFamily: 'Sora, sans-serif', fontWeight: 800, fontSize: '1.4rem', color: 'var(--navy)' }}>{val}</div>
              <div style={{ fontSize: '0.78rem', color: 'var(--gray-600)', marginTop: 2 }}>{label}</div>
            </div>
          ))}
        </div>
      </FadeUp>

      {/* 3 rows of infinite scrolling — alternating directions */}
      <div style={{ position: 'relative' }}>
        {/* fade edges */}
        <div style={{ position: 'absolute', top: 0, left: 0, bottom: 0, width: 100, background: 'linear-gradient(to right, #fff, transparent)', zIndex: 2, pointerEvents: 'none' }} />
        <div style={{ position: 'absolute', top: 0, right: 0, bottom: 0, width: 100, background: 'linear-gradient(to left, #fff, transparent)', zIndex: 2, pointerEvents: 'none' }} />
        <TestimonialRow items={TROW1} direction={1}  speed={42} />
        <TestimonialRow items={TROW2} direction={-1} speed={50} />
      </div>
    </section>
  );
}

function Partnership() {
  const navigate = useNavigate();
  const goContact = () => navigate('/contact');
  const dotStyle = { width: 6, height: 6, borderRadius: '50%', background: 'var(--gold)', flexShrink: 0, marginTop: 8 };
  const liStyle = { display: 'flex', alignItems: 'flex-start', gap: '0.65rem', fontSize: '0.95rem', color: 'var(--gray-600)', lineHeight: 1.65 };
  const liStyleWhite = { ...liStyle, color: 'rgba(255,255,255,0.8)' };

  return (
    <section id="partnership" style={{ background: 'var(--gray-50)', padding: '5rem 2.5rem' }}>
      <div style={{ maxWidth: 1100, margin: '0 auto' }}>
        <FadeUp>
          <div style={{ textAlign: 'center', marginBottom: '3rem' }}>
            <span style={{ fontSize: '0.75rem', fontWeight: 700, letterSpacing: '0.18em', textTransform: 'uppercase', color: 'var(--gold)', display: 'block', marginBottom: '0.6rem' }}>Work With Us</span>
            <h2 style={{ fontFamily: 'Sora, sans-serif', fontSize: 'clamp(1.75rem, 3vw, 2.5rem)', fontWeight: 800, color: 'var(--navy)' }}>Grow Together</h2>
          </div>
        </FadeUp>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '1.5rem', alignItems: 'stretch' }}>
          <FadeUp>
            <div style={{ height: '100%' }}>
              <div style={{ background: 'var(--navy)', borderRadius: 16, padding: '2.5rem', height: '100%', display: 'flex', flexDirection: 'column', boxSizing: 'border-box' }}>
                <span style={{ fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--gold)', display: 'block', marginBottom: '0.6rem' }}>For Installers & Contractors</span>
                <h3 style={{ fontFamily: 'Sora, sans-serif', color: '#fff', fontSize: '1.35rem', fontWeight: 800, marginBottom: '0.75rem', lineHeight: 1.25 }}>Trusted by CA's Best Installers</h3>
                <p style={{ color: 'rgba(255,255,255,0.65)', fontSize: '0.875rem', lineHeight: 1.65, marginBottom: '1.25rem' }}>We send you qualified clients. You deliver great installs. Simple.</p>
                <ul style={{ listStyle: 'none', display: 'flex', flexDirection: 'column', gap: '0.65rem', marginBottom: '2rem', flex: 1 }}>
                  {['Licensed contractors only','Pre-qualified leads, ready to move','Full financial analysis done for you','Brand-agnostic, zero conflict'].map((text, i) => (
                    <li key={i} style={liStyleWhite}><span style={{ ...dotStyle, background: 'var(--gold)' }} />{text}</li>
                  ))}
                </ul>
                <button onClick={goContact} style={{ background: 'var(--gold)', color: 'var(--navy)', border: 'none', padding: '0.8rem 1.6rem', borderRadius: 50, fontFamily: 'Sora, sans-serif', fontWeight: 700, fontSize: '0.875rem', cursor: 'pointer', alignSelf: 'flex-start' }}>Join Our Network →</button>
              </div>
            </div>
          </FadeUp>
          <FadeUp delay={0.15}>
            <div style={{ height: '100%' }}>
              <div style={{ background: '#fff', border: '1.5px solid var(--gray-200)', borderRadius: 16, padding: '2.5rem', height: '100%', display: 'flex', flexDirection: 'column', boxSizing: 'border-box' }}>
                <span style={{ fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--gold)', display: 'block', marginBottom: '0.6rem' }}>For Businesses & Investors</span>
                <h3 style={{ fontFamily: 'Sora, sans-serif', color: 'var(--navy)', fontSize: '1.35rem', fontWeight: 800, marginBottom: '0.75rem', lineHeight: 1.25 }}>Built for Serious Partners</h3>
                <p style={{ color: 'var(--gray-600)', fontSize: '0.875rem', lineHeight: 1.65, marginBottom: '1.25rem' }}>We're growing fast across California. Let's build together.</p>
                <ul style={{ listStyle: 'none', display: 'flex', flexDirection: 'column', gap: '0.65rem', marginBottom: '2rem', flex: 1 }}>
                  {['BESS project co-investment','Financing & lending partnerships','Co-marketing opportunities','White-label solutions available'].map((text, i) => (
                    <li key={i} style={liStyle}><span style={dotStyle} />{text}</li>
                  ))}
                </ul>
                <button onClick={goContact} style={{ background: 'var(--navy)', color: '#fff', border: 'none', padding: '0.8rem 1.6rem', borderRadius: 50, fontFamily: 'Sora, sans-serif', fontWeight: 700, fontSize: '0.875rem', cursor: 'pointer', alignSelf: 'flex-start' }}>Explore Partnership →</button>
              </div>
            </div>
          </FadeUp>
        </div>
      </div>
    </section>
  );
}

function Insights() {
  const [articles, setArticles] = useState([]);

  useEffect(() => {
    async function load() {
      try {
        const q = query(
          collection(db, 'posts'),
          where('published', '==', true),
          orderBy('createdAt', 'desc'),
          limit(12)
        );
        const snap = await getDocs(q);
        setArticles(snap.docs.map(d => d.data()));
      } catch (e) {
        console.error('Failed to load articles:', e);
      }
    }
    load();
  }, []);

  if (articles.length === 0) return null;

  return (
    <section id="insights" style={{ background: 'var(--gray-50)', padding: '5rem 0' }}>
      <div style={{ maxWidth: 1100, margin: '0 auto', paddingLeft: '2.5rem', paddingRight: '2.5rem' }}>
        <FadeUp>
          <div style={{ textAlign: 'center', marginBottom: '3rem' }}>
            <span style={{ fontSize: '0.75rem', fontWeight: 700, letterSpacing: '0.18em', textTransform: 'uppercase', color: 'var(--gold)', display: 'block', marginBottom: '0.6rem' }}>Learn</span>
            <h2 style={{ fontFamily: 'Sora, sans-serif', fontSize: 'clamp(1.75rem, 3vw, 2.5rem)', fontWeight: 800, color: 'var(--navy)', marginBottom: '0.75rem' }}>Solar Insights</h2>
            <p style={{ fontSize: '1rem', color: 'var(--gray-600)', maxWidth: 540, margin: '0 auto', lineHeight: 1.7 }}>Latest on solar incentives, technology, and savings strategies.</p>
          </div>
        </FadeUp>
      </div>
      <BlogCarousel articles={articles} />
    </section>
  );
}

function BlogCarousel({ articles }) {
  const trackRef = React.useRef(null);
  const pausedRef = React.useRef(false);
  const posRef = React.useRef(0);
  const rafRef = React.useRef(null);
  const speedRef = React.useRef(0.5);
  const CARD_W = 336; // card width + gap

  // Deduplicate by slug, then duplicate for seamless loop
  const unique = articles.filter((a, i, arr) => arr.findIndex(x => x.slug === a.slug) === i);
  const items = [...unique, ...unique];

  React.useEffect(() => {
    const track = trackRef.current;
    if (!track) return;
    const halfWidth = track.scrollWidth / 2;

    const animate = () => {
      if (!pausedRef.current) {
        posRef.current += speedRef.current;
        if (posRef.current >= halfWidth) posRef.current = 0;
        track.style.transform = `translateX(-${posRef.current}px)`;
      }
      rafRef.current = requestAnimationFrame(animate);
    };
    rafRef.current = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(rafRef.current);
  }, []);

  const skip = (dir) => {
    const track = trackRef.current;
    if (!track) return;
    const halfWidth = track.scrollWidth / 2;
    posRef.current = ((posRef.current + dir * CARD_W) % halfWidth + halfWidth) % halfWidth;
    track.style.transform = `translateX(-${posRef.current}px)`;
  };

  return (
    <div style={{ position: 'relative' }}>
      {/* Left arrow */}
      <button onClick={() => skip(-1)}
        onMouseEnter={e => { e.currentTarget.style.opacity = '1'; e.currentTarget.style.transform = 'translateY(-50%) scale(1.1)'; }}
        onMouseLeave={e => { e.currentTarget.style.opacity = '0.7'; e.currentTarget.style.transform = 'translateY(-50%) scale(1)'; }}
        style={{ position: 'absolute', left: '1rem', top: '50%', transform: 'translateY(-50%)', zIndex: 10, background: 'var(--navy)', color: '#fff', border: 'none', borderRadius: '50%', width: 44, height: 44, fontSize: '1.2rem', cursor: 'pointer', opacity: 0.7, transition: 'all 0.2s', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 4px 16px rgba(26,43,95,0.25)' }}>‹</button>

      {/* Right arrow */}
      <button onClick={() => skip(1)}
        onMouseEnter={e => { e.currentTarget.style.opacity = '1'; e.currentTarget.style.transform = 'translateY(-50%) scale(1.1)'; }}
        onMouseLeave={e => { e.currentTarget.style.opacity = '0.7'; e.currentTarget.style.transform = 'translateY(-50%) scale(1)'; }}
        style={{ position: 'absolute', right: '1rem', top: '50%', transform: 'translateY(-50%)', zIndex: 10, background: 'var(--navy)', color: '#fff', border: 'none', borderRadius: '50%', width: 44, height: 44, fontSize: '1.2rem', cursor: 'pointer', opacity: 0.7, transition: 'all 0.2s', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 4px 16px rgba(26,43,95,0.25)' }}>›</button>

      <div style={{ overflow: 'hidden', width: '100%', padding: '0.5rem 0' }}>
        <div ref={trackRef} style={{ display: 'flex', gap: '1.5rem', width: 'max-content', willChange: 'transform', alignItems: 'stretch' }}>
          {items.map((a, i) => (
            <div key={i}
              onMouseEnter={() => { pausedRef.current = true; speedRef.current = 0; }}
              onMouseLeave={() => { pausedRef.current = false; speedRef.current = 0.5; }}
              style={{ width: 320, flexShrink: 0 }}>
              <ArticleCard article={a} />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function ArticleCard({ article }) {
  const [hovered, setHovered] = useState(false);
  return (
    <Link to={`/blog/${article.slug}`} style={{ textDecoration: 'none', display: 'block', height: '100%' }}>
      <div onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)}
        style={{ background: '#fff', borderRadius: 16, overflow: 'hidden', boxShadow: hovered ? '0 16px 40px rgba(26,43,95,0.12)' : '0 4px 20px rgba(26,43,95,0.06)', transform: hovered ? 'translateY(-4px)' : 'none', transition: 'all 0.3s', cursor: 'pointer', display: 'flex', flexDirection: 'column', height: 420 }}>
        {/* Image */}
        <div style={{ height: 185, flexShrink: 0, backgroundImage: `url(${article.img})`, backgroundSize: 'cover', backgroundPosition: 'center', position: 'relative' }}>
          <span style={{ position: 'absolute', top: '1rem', left: '1rem', background: 'var(--gold)', color: 'var(--navy)', fontSize: '0.68rem', fontWeight: 700, padding: '0.3rem 0.7rem', borderRadius: 50, letterSpacing: '0.06em', textTransform: 'uppercase' }}>{article.tag}</span>
        </div>
        {/* Body */}
        <div style={{ padding: '1.25rem 1.5rem', display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
          <h3 style={{ fontFamily: 'Sora, sans-serif', fontWeight: 700, fontSize: '0.97rem', color: 'var(--navy)', marginBottom: '0.5rem', lineHeight: 1.45, textDecoration: hovered ? 'underline' : 'none', overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', flexShrink: 0 }}>{article.title}</h3>
          <p style={{ fontSize: '0.85rem', color: 'var(--gray-600)', lineHeight: 1.6, flex: 1, overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', margin: 0 }}>{article.excerpt}</p>
          {/* Footer always at bottom */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingTop: '0.75rem', marginTop: '0.75rem', borderTop: '1px solid var(--gray-100)', flexShrink: 0 }}>
            <span style={{ fontSize: '0.75rem', color: 'var(--gray-400)' }}>{article.date}</span>
            <span style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--gold)' }}>Read More →</span>
          </div>
        </div>
      </div>
    </Link>
  );
}

export default function Home() {
  return (
    <>
      <Helmet>
        <title>Solar Savings Calculator – Estimate Your Solar Savings in California | SolarSavings</title>
        <meta name="description" content="Free solar savings calculator for California homeowners & businesses. See how much you can save with solar panels — no cost, no commitment." />
        <link rel="canonical" href="https://solarsavings.com/" />
      </Helmet>
      <Hero />
      <ServiceCards />
      <Brands />
      <Testimonials />
      <Partnership />
      <Insights />
    </>
  );
}
